import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import TechnicalAnalysisDashboard from "./TechnicalAnalysisDashboard";

interface QuantTickerData {
  ticker: string;
  issuer_name?: string;
  sector?: string;
  deal_type?: string;
  quant_analysis?: object | string;
  [key: string]: any;
}

const QuantAnalysisDetailsModal: React.FC = () => {
  const { ticker: rawTicker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationSearch = new URLSearchParams(location.search);
  const returnPage = locationSearch.get("page") || "0";
  const returnRowsPerPage = locationSearch.get("rowsPerPage") || "10";

  const ticker = rawTicker ? decodeURIComponent(rawTicker) : "";

  console.log("[QuantAnalysisDetailsModal] mounted, ticker:", ticker);

  const [tickerData, setTickerData] = useState<QuantTickerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  console.log("[QuantAnalysisDetailsModal] apiUrl:", apiUrl);

  useEffect(() => {
    console.log("[QuantAnalysisDetailsModal] useEffect fired, ticker:", ticker);
    if (ticker) {
      fetchDetails();
    }
  }, [ticker]);

  const fetchDetails = async () => {
    if (!apiUrl) {
      setError("REACT_APP_API_URL is not configured.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token");

      const res = await fetch(`${apiUrl}/api/quant_agent/all_tickers_latest/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ ticker, deal_type: "FO" }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error || data?.detail || `Request failed with status ${res.status}`
        );
      }

      if (data.data) {
        const info: QuantTickerData = Array.isArray(data.data) ? data.data[0] : data.data;
        if (info && typeof info.quant_analysis === "string") {
          try {
            info.quant_analysis = JSON.parse(info.quant_analysis);
          } catch (e) {
            console.error("Failed to parse quant_analysis:", e);
          }
        }
        setTickerData(info);
      } else {
        setError("No data found for this ticker.");
      }
    } catch (err: any) {
      setError(err?.message || "Unable to load quant analysis.");
    } finally {
      setLoading(false);
    }
  };

  const technicalData =
    tickerData && typeof tickerData.quant_analysis === "object"
      ? (tickerData.quant_analysis as any)
      : null;

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/quant_agent?deal_type=FO&page=${returnPage}&rowsPerPage=${returnRowsPerPage}`)}
        sx={{
          textTransform: "none",
          color: "#4f46e5",
          fontSize: "0.85rem",
          fontWeight: 600,
          mb: 1.5,
          "&:hover": { background: "#eef2ff" },
        }}
      >
        Back to Quant Analysis
      </Button>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 12, gap: 2 }}>
          <CircularProgress size={48} thickness={4} sx={{ color: "#4f46e5" }} />
          <Typography sx={{ fontSize: "0.9rem", color: "#94a3b8", fontWeight: 500 }}>
            Loading quant analysis for {ticker}...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5, border: "1px solid #fecaca" }}>
          {error}
        </Alert>
      )}

      {/* Dashboard */}
      {!loading && !error && technicalData && (
        <TechnicalAnalysisDashboard data={technicalData} runDate={tickerData?.run_date} />
      )}

      {/* No data */}
      {!loading && !error && tickerData && !technicalData && (
        <Box
          sx={{
            py: 12,
            textAlign: "center",
            background: "#ffffff",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
          }}
        >
          <InsightsRoundedIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 2 }} />
          <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
            No technical analysis data available for this ticker.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuantAnalysisDetailsModal;
